<template>
  <div>
    <div class="bg-primary-100 flex items-center justify-between px-4 py-2">
      <div class="flex items-center">
        <w-button
          size="func"
          @click="toggleAllDetail = !toggleAllDetail"
        >
          {{ isAllDetailOpened ? '收合詳細內容' : '展開詳細內容' }}
        </w-button>
        <span class="ml-2 text-sm">
          點擊可以一併展開人員訂購品項資訊
        </span>
      </div>
      <w-button
        size="func"
        type="link"
        @click="showPaidAllConfirmDialog = true"
      >
        <i class="icon-money mr-2" />
        <span>全部收款</span>
      </w-button>
    </div>
    <b-table
      :data="orderedMembers"
      :opened-detailed="openedRowDetail"
      :row-class="itemRowClass"
      :show-detail-icon="false"
      class="member-table"
      custom-detail-row
      default-sort="name"
      detail-key="id"
      detailed
      hoverable
      scrollable
    >
      <b-table-column
        :th-attrs="columnThAttrs"
        field="index"
        label="No."
        v-slot="props"
        width="28px"
      >
        {{ props.index + 1 }}.
      </b-table-column>
      <b-table-column
        :th-attrs="columnThAttrs"
        field="name"
        label="姓名"
        sortable
        v-slot="props"
        width="200px"
      >
        <div class="flex items-center justify-between p-0">
          <div>
            <i v-if="props.row['onSiteOrder']" class="icon-order mr-2 text-primary-500" />
            <label>{{ props.row.name }}</label>
          </div>
          <a
            href="#"
            @click.prevent="props.toggleDetails(props.row)"
          >
            <font-awesome-icon
              :icon="openedRowDetail.includes(props.row.id) ? 'chevron-up' : 'chevron-down'"
            />
          </a>
        </div>
      </b-table-column>
      <b-table-column
        :th-attrs="columnThAttrs"
        field="department_name"
        label="單位"
        sortable
        v-slot="props"
        width="200px"
      >
        {{ props.row.department_name }}
      </b-table-column>
      <b-table-column
        :th-attrs="columnThAttrs"
        field="ext"
        label="分機/電話"
        sortable
        v-slot="props"
        width="100px"
      >
        {{ props.row.ext }}
      </b-table-column>
      <b-table-column
        :th-attrs="columnThAttrs"
        field="receiving_status"
        label="領取狀態"
        sortable
        v-slot="props"
        width="88px"
      >
        <span :class="{
          'text-pink-500': props.row.receiving_status === receivingStatus.UNRECEIVED
        }">
          {{ getReceivingStatusText(props.row.receiving_status) }}
        </span>
      </b-table-column>
      <b-table-column
        :th-attrs="columnThAttrs"
        centered
        field="onSiteOrder"
        label="現場訂購"
        sortable
        v-slot="props"
        width="88px"
      >
        <i
          v-if="props.row['onSiteOrder']"
          class="icon-check-thin text-xl text-red-500"
        />
      </b-table-column>
      <b-table-column
        :th-attrs="columnThAttrs"
        cell-class="text-right"
        field="subtotal"
        header-class="text-right"
        label="小計"
        sortable
        v-slot="props"
        width="100px"
      >
        <span :class="getTotalColor(props.row)">
          {{ props.row.subtotal | commaFormat | priceFormat }}
        </span>
      </b-table-column>
      <b-table-column
        :th-attrs="columnThAttrs"
        field="paid"
        label="收款金額"
        width="160px"
        v-slot="props"
      >
        <div class="level p-0">
          <b-field
            :type="props.row.paid > props.row.subtotal ? 'is-danger' : undefined"
            class="mb-0"
          >
            <b-input
              :value="props.row.paid"
              size="is-small"
              icon="dollar-sign"
              @input="updatePaid(props.row, $event)"
            />
          </b-field>
          <w-button
            v-if="props.row.paid === props.row.subtotal"
            class="ml-2 text-2xl"
            type="icon"
            @click="updatePaid(props.row, 0)"
          >
            <i class="icon-check-blue" />
          </w-button>
          <w-button
            v-else
            class="ml-2 text-2xl"
            type="icon"
            @click="updatePaid(props.row, props.row.subtotal)"
          >
            <i class="icon-check-gray" />
          </w-button>
        </div>
      </b-table-column>
      <b-table-column
        v-if="allowCancel"
        :th-attrs="columnThAttrs"
        centered
        field="cancelOrder"
        label="刪除訂單"
        width="80px"
        v-slot="props"
      >
        <w-button
          class="hover:text-red-500 mr-1 text-gray-500"
          type="icon"
          @click="onCancelOrder(props.row)"
        >
          <i class="icon-trash" />
        </w-button>
      </b-table-column>

      <template #detail="props">
        <tr
          v-for="(item, itemIndex) in props.row.member_order_items"
          :key="`member-${item.uuid}-${props.row.id}-${itemIndex}`"
          class="detail-row">
          <td class="detail-row__indicator">&nbsp;</td>
          <td>
            <span class="detail-row__amount">{{ item.quantity || '0' }}x</span>
            <span class="mx-4">{{ item.entree.name }}</span>
          </td>
          <td colspan="2">
            <span class="leading-6">
              <template v-for="option in sortOptions(item.entree.options)">
                <span
                  v-for="item in option.items"
                  :key="`option-item-${item.index}`"
                  class="option"
                >
                  {{ item.name }}{{ item.price ? formatPrice(item.price) : '' }}
                </span>
              </template>
              {{ item.note ? `#${item.note}` : '' }}
            </span>
          </td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>
            <span>
              {{ item.subtotal | commaFormat | priceFormat }}
            </span>
          </td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
      </template>

      <template #empty>
        <p class="my-12 text-center text-gray-700 text-sm">無已訂人員</p>
      </template>
    </b-table>
    <paid-all
      v-model="showPaidAllConfirmDialog"
      :orderId="orderId"
    />
    <menu-common-dialog
      :loading="isCancelingOrder"
      cancel-text="保留訂單"
      confirm-text="刪除整筆訂單"
      title="刪除訂單"
      v-model="showOnDeleteOrderDialog"
      @cancel="onCloseDeleteOrderDialog"
      @close="onCloseDeleteOrderDialog"
      @confirm="onConfirmCancelOrder(rowInfos)"
    >
      <template #content>
        <p class="text-left">訂單刪除後即無法恢復，如需重新訂購需請訂購人重新下單。</p>
      </template>
      <section class="flex flex-col items-start mb-4 mt-6">
        <label class="font-medium text-sm">訂單資訊</label>
        <div class="bg-light-gray-500 mt-2 p-2 rounded text-left w-full">
          <p :title="rowInfos.name" class="mb-1 truncate w-full">訂購人：{{ rowInfos.name }}</p>
          <p
            :title="getOrderItemsText(rowInfos.member_order_items)"
            class="line-clamp-3 mb-0 w-full"
          >
            訂購品項：{{ getOrderItemsText(rowInfos.member_order_items) }}
          </p>
        </div>
      </section>
    </menu-common-dialog>
  </div>
</template>

<script>
import OrderMemberTable from './OrderMemberTable';

export default OrderMemberTable;
</script>

<style lang="scss" scoped>
@import "./OrderMemberTable";
</style>
